import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import accountApi from '../../services/accountApi';

async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) {
    throw new Error('clipboard_unavailable');
  }
}

function isInvitationCopyable(inv) {
  if (!inv || inv.status !== 'pending') return false;
  if (!inv.expires_at) return true;
  return new Date(inv.expires_at).getTime() > Date.now();
}

function TeamPage() {
  const { activeTenantId, hasTenantPermission } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [devInviteToken, setDevInviteToken] = useState(null);
  const [devAcceptUrl, setDevAcceptUrl] = useState(null);
  const [roleUpdating, setRoleUpdating] = useState(null);
  const [removing, setRemoving] = useState(null);

  const [inviteMessage, setInviteMessage] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [lastCreatedAcceptUrl, setLastCreatedAcceptUrl] = useState(null);
  const [manualCopyUrl, setManualCopyUrl] = useState(null);
  const [resending, setResending] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [copying, setCopying] = useState(null);

  const canInvite = hasTenantPermission('member.invite');
  const canRemove = hasTenantPermission('member.remove');
  const canManageRoles = hasTenantPermission('member.manage_roles');

  const clearSensitiveInviteState = () => {
    setLastCreatedAcceptUrl(null);
    setManualCopyUrl(null);
    setDevInviteToken(null);
    setDevAcceptUrl(null);
    setCopyFeedback(null);
  };

  const loadTeam = async (tenantId) => {
    const [memberRows, inviteRows, catalog] = await Promise.all([
      accountApi.listMembers(tenantId),
      accountApi.listInvitations(tenantId),
      accountApi.getTenantRoleCatalog(tenantId),
    ]);
    setMembers(memberRows);
    setInvitations(inviteRows);
    const roles = (catalog && catalog.assignable_roles) || [];
    setAssignableRoles(roles);
    if (roles.length > 0 && !roles.includes(inviteRole)) {
      setInviteRole(roles[0]);
    }
  };

  useEffect(() => {
    if (!activeTenantId) return;
    let cancelled = false;
    clearSensitiveInviteState();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadTeam(activeTenantId);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load team');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearSensitiveInviteState();
    };
    // inviteRole intentionally omitted — only used for defaulting the invite dropdown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const deliveryMessage = (delivery) => {
    if (!delivery) return null;
    const provider = delivery.provider || '';
    if (delivery.status === 'sent') {
      if (provider === 'mailgun') {
        return 'Invitation created and email sent. Inbox delivery is not guaranteed by API acceptance alone.';
      }
      return 'Invitation created and email sent.';
    }
    if (delivery.status === 'development_only') {
      return 'Invitation created. Email delivery uses the development backend (not a real inbox).';
    }
    if (delivery.status === 'failed') {
      return 'The invitation was created, but the email could not be delivered. Copy the invite link and send it manually.';
    }
    return null;
  };

  const applyCopiedUrl = async (url, { rotated } = {}) => {
    try {
      await copyTextToClipboard(url);
      setManualCopyUrl(null);
      setCopyFeedback(
        rotated
          ? 'Invite link copied. Copying a new link invalidates any previously issued link for this invitation.'
          : 'Invite link copied'
      );
    } catch {
      setManualCopyUrl(url);
      setCopyFeedback('The invite link could not be copied. Please try again.');
      setError('The invite link could not be copied. Please try again.');
    }
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!canInvite || !activeTenantId) return;
    setInviting(true);
    setError(null);
    setInviteMessage(null);
    setCopyFeedback(null);
    setManualCopyUrl(null);
    try {
      const created = await accountApi.createInvitation(activeTenantId, {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteEmail('');
      setDevInviteToken(created && created.development_token ? created.development_token : null);
      setDevAcceptUrl(
        created && created.development_accept_url ? created.development_accept_url : null
      );
      const acceptUrl =
        (created && created.acceptance_url) ||
        (created && created.development_accept_url) ||
        null;
      setLastCreatedAcceptUrl(acceptUrl);
      setInviteMessage(deliveryMessage(created && created.delivery) || 'Invitation created.');
      await loadTeam(activeTenantId);
    } catch (err) {
      setLastCreatedAcceptUrl(null);
      setError(err.message || 'Invitation could not be created.');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyCreatedLink = async () => {
    if (!lastCreatedAcceptUrl || copying) return;
    setCopying('created');
    setError(null);
    try {
      await applyCopiedUrl(lastCreatedAcceptUrl, { rotated: false });
    } finally {
      setCopying(null);
    }
  };

  const handleCopyInviteLink = async (invitation) => {
    if (!canInvite || !activeTenantId || !invitation || copying) return;
    setCopying(invitation.id);
    setError(null);
    setCopyFeedback(null);
    setManualCopyUrl(null);
    try {
      const result = await accountApi.copyPartnerInvitationLink(activeTenantId, invitation.id);
      const url = result && result.acceptance_url;
      if (!url) {
        throw new Error('The invitation link could not be generated.');
      }
      await applyCopiedUrl(url, { rotated: Boolean(result.token_rotated) });
      await loadTeam(activeTenantId);
    } catch (err) {
      const code = err && err.code;
      const sanitized =
        code === 'invitation_expired'
          ? 'This invitation has expired.'
          : code === 'invitation_not_pending'
            ? 'Only pending invitations have a copyable link.'
            : err.message || 'The invitation link could not be generated.';
      setError(sanitized);
      try {
        await loadTeam(activeTenantId);
      } catch {
        /* ignore refresh errors */
      }
    } finally {
      setCopying(null);
    }
  };

  const handleResend = async (invitationId) => {
    if (!canInvite || !activeTenantId) return;
    setResending(invitationId);
    setError(null);
    setInviteMessage(null);
    try {
      const result = await accountApi.resendInvitation(activeTenantId, invitationId);
      setDevInviteToken(result && result.development_token ? result.development_token : null);
      setDevAcceptUrl(
        result && result.development_accept_url ? result.development_accept_url : null
      );
      if (result && result.acceptance_url) {
        setLastCreatedAcceptUrl(result.acceptance_url);
      }
      const msg = deliveryMessage(result && result.delivery);
      setInviteMessage(
        msg
          ? msg
              .replace('Invitation created', 'Invitation resent')
              .replace('Invitation email accepted', 'Invitation email resent and accepted')
          : 'Invitation resent.'
      );
      await loadTeam(activeTenantId);
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
    } finally {
      setResending(null);
    }
  };

  const handleRevoke = async (invitationId) => {
    if (!canRemove || !activeTenantId) return;
    if (!window.confirm('Revoke this invitation? The previous invite link will stop working.')) {
      return;
    }
    setRevoking(invitationId);
    setError(null);
    try {
      await accountApi.revokeInvitation(activeTenantId, invitationId);
      setInviteMessage('Invitation revoked.');
      setLastCreatedAcceptUrl(null);
      await loadTeam(activeTenantId);
    } catch (err) {
      setError(err.message || 'Failed to revoke invitation');
    } finally {
      setRevoking(null);
    }
  };

  const handleRoleChange = async (membershipId, role) => {
    if (!canManageRoles || !activeTenantId) return;
    setRoleUpdating(membershipId);
    setError(null);
    try {
      await accountApi.updateMemberRole(activeTenantId, membershipId, role);
      await loadTeam(activeTenantId);
    } catch (err) {
      setError(err.message || 'Failed to update role');
    } finally {
      setRoleUpdating(null);
    }
  };

  const handleRemoveMember = async (membershipId) => {
    if (!canRemove || !activeTenantId) return;
    setRemoving(membershipId);
    setError(null);
    try {
      await accountApi.removeMember(activeTenantId, membershipId);
      await loadTeam(activeTenantId);
    } catch (err) {
      setError(err.message || 'Failed to remove member');
    } finally {
      setRemoving(null);
    }
  };

  const inviteRoleOptions =
    assignableRoles.length > 0
      ? assignableRoles
      : ['viewer', 'developer', 'technical_artist', 'project_manager', 'billing_manager', 'admin'];

  return (
    <div>
      <h1 className="product-page-title">Team</h1>
      <p className="product-page-subtitle">Members and pending invitations.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {inviteMessage && <Alert variant="info">{inviteMessage}</Alert>}
      <div aria-live="polite" className="visually-hidden">
        {copyFeedback || ''}
      </div>
      {copyFeedback && (
        <Alert variant={manualCopyUrl ? 'warning' : 'success'} onClose={() => setCopyFeedback(null)} dismissible>
          {copyFeedback}
        </Alert>
      )}
      {lastCreatedAcceptUrl && canInvite && (
        <Alert variant="secondary">
          <p className="mb-2">
            Anyone with this link may be able to accept the invitation until it expires. Share it
            only with the intended recipient.
          </p>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={copying === 'created'}
            onClick={handleCopyCreatedLink}
          >
            {copying === 'created' ? 'Copying…' : 'Copy Invite Link'}
          </Button>
        </Alert>
      )}
      {manualCopyUrl && (
        <Alert variant="warning" onClose={() => setManualCopyUrl(null)} dismissible>
          <Form.Label htmlFor="manual-invite-link">Select and copy this invite link</Form.Label>
          <Form.Control
            id="manual-invite-link"
            readOnly
            value={manualCopyUrl}
            onFocus={(e) => e.target.select()}
          />
        </Alert>
      )}
      {devInviteToken && (
        <Alert variant="warning">
          Development-only invitation token (shown once; copy now):{' '}
          <code>{devInviteToken}</code>
        </Alert>
      )}
      {devAcceptUrl && (
        <Alert variant="warning">
          Development-only accept URL: <a href={devAcceptUrl}>{devAcceptUrl}</a>
        </Alert>
      )}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading team…
        </div>
      )}

      {!loading && (
        <>
          <div className="product-card">
            <h2 className="h6 mb-3">Members</h2>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  {(canManageRoles || canRemove) && <th />}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>{m.display_name || m.email}</td>
                    <td>{m.email}</td>
                    <td>
                      {canManageRoles && assignableRoles.length > 0 && m.role !== 'owner' ? (
                        <Form.Select
                          size="sm"
                          value={m.role}
                          disabled={roleUpdating === m.id}
                          onChange={(e) => handleRoleChange(m.id, e.target.value)}
                        >
                          {assignableRoles.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        m.role
                      )}
                    </td>
                    <td>{m.status}</td>
                    {(canManageRoles || canRemove) && (
                      <td>
                        {canRemove && m.role !== 'owner' && m.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={removing === m.id}
                            onClick={() => handleRemoveMember(m.id)}
                          >
                            {removing === m.id ? 'Removing…' : 'Remove'}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="product-card">
            <h2 className="h6 mb-3">Invitations</h2>
            <p className="small text-muted mb-2">
              Copying a new link from the list invalidates any previously issued link for that
              invitation.
            </p>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Delivery</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  {(canInvite || canRemove) && <th />}
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>{inv.role}</td>
                    <td>{inv.status}</td>
                    <td className="small">
                      {inv.delivery?.status || inv.last_delivery_status || '—'}
                      {inv.send_count ? ` · ${inv.send_count}×` : ''}
                    </td>
                    <td className="small">
                      {inv.last_sent_at
                        ? new Date(inv.last_sent_at).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      {inv.expires_at
                        ? new Date(inv.expires_at).toLocaleString()
                        : '—'}
                    </td>
                    {(canInvite || canRemove) && (
                      <td className="text-nowrap">
                        {isInvitationCopyable(inv) && canInvite && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            aria-label={`Copy invite link for ${inv.email}`}
                            disabled={copying === inv.id || resending === inv.id}
                            onClick={() => handleCopyInviteLink(inv)}
                          >
                            {copying === inv.id ? 'Copying…' : 'Copy Invite Link'}
                          </Button>
                        )}
                        {inv.status === 'pending' && canInvite && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            disabled={resending === inv.id || copying === inv.id}
                            onClick={() => handleResend(inv.id)}
                          >
                            {resending === inv.id ? 'Resending…' : 'Resend'}
                          </Button>
                        )}
                        {inv.status === 'pending' && canRemove && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={revoking === inv.id}
                            onClick={() => handleRevoke(inv.id)}
                          >
                            {revoking === inv.id ? 'Revoking…' : 'Revoke'}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {canInvite && (
            <div className="product-card">
              <h2 className="h6 mb-3">Invite member</h2>
              <Form onSubmit={handleInvite} className="row g-2 align-items-end">
                <div className="col-md-5">
                  <Form.Label htmlFor="invite-email">Email</Form.Label>
                  <Form.Control
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label htmlFor="invite-role">Role</Form.Label>
                  <Form.Select
                    id="invite-role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    {inviteRoleOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-3">
                  <Button type="submit" disabled={inviting}>
                    {inviting ? 'Sending…' : 'Send invite'}
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TeamPage;
